/**
 * Idempotency-Key generation (audit phase 09 §5).
 *
 * One UUID per LOGICAL operation — not per HTTP attempt.  Retries over a
 * flaky network reuse the same key so the backend replays the cached
 * response (24 h Redis TTL) instead of creating a duplicate row.
 *
 * Callers that already have a natural idempotency identifier (e.g. a
 * file SHA + user UUID for uploads) should pass that instead of a fresh
 * UUID — matches the backend examples like
 *   upload-<org_id>-<sha256-of-file>-<uuid>
 */

/** Random v4 UUID via `crypto.randomUUID()` with a polyfilled fallback. */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback — only hit on very old runtimes.  RFC 4122 v4.
  const rand = (): number => Math.floor(Math.random() * 16);
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (c) => {
    const r = rand();
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** HTTP methods that should carry an Idempotency-Key by default. */
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function isMutation(method?: string): boolean {
  if (!method) return false;
  return MUTATION_METHODS.has(method.toUpperCase());
}
